import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { presentUnexpectedFailure } from '../../../shared/sentry/presentUnexpectedFailure';
import { parseDecimalInput } from '../../../shared/numeric/localeNumeric';
import { showError, showSuccess } from '../../../shared/ui/appToast';
import type { CategoryAppliesTo, CategoryRecord } from '../../domain/categoryTypes';
import type { CategoryFilterState } from '../../domain/catalogCategoryTypes';
import { buildCategoryOptions } from '../../domain/categoryOptions';
import {
  createCategory,
  reorderCategorySortOrders,
  setCategoryActive,
  updateCategory,
} from '../../infra/categoriesRepoMutations';
import { listCategories } from '../../infra/categoriesRepoRead';
import { pickProductImage, isProductImageValidationError } from '../../infra/pickProductImage';
import { optimizeProductImage } from '../../infra/optimizeProductImage';
import { uploadCategoryImage, removeCategoryImage } from '../../infra/categoryImageRepo';
import { requireSessionOrganizationId } from '../../../shell/session/sessionOrganization';
import { subscribeCatalogSynced } from '../../../sync/core/catalogSyncListeners';

export type CategoryDraft = {
  name: string;
  appliesTo: CategoryAppliesTo;
  parentId: string | null;
  code: string;
  defaultMarginPercent: string;
  description: string;
  imageUrl: string;
  isRestricted: boolean;
};

export const emptyCategoryDraft = (): CategoryDraft => ({
  name: '',
  appliesTo: 'product',
  parentId: null,
  code: '',
  defaultMarginPercent: '',
  description: '',
  imageUrl: '',
  isRestricted: false,
});

const toDraft = (category?: CategoryRecord | null): CategoryDraft => ({
  name: category?.name ?? '',
  appliesTo: category?.appliesTo ?? 'product',
  parentId: category?.parentId ?? null,
  code: category?.code ?? '',
  defaultMarginPercent: category?.defaultMarginPercent?.toString() ?? '',
  description: category?.description ?? '',
  imageUrl: category?.imageUrl ?? '',
  isRestricted: category?.isRestricted === true,
});

export type ToggleCategoryActiveOptions = {
  filterState?: CategoryFilterState;
  childCount?: number;
  /** IDs bloqueados mientras persiste el toggle (padre + hijos en cascade). */
  togglingIds?: string[];
  /** Al activar una subcategoría se activó también la categoría padre. */
  wokeParent?: boolean;
};

export type UseCatalogCategoryManagerOptions = {
  onCategoriesChanged?: () => void;
  /** No carga ni escucha la lista propia (el caller ya tiene su fuente, p. ej. masters). */
  skipListLoad?: boolean;
};

export const useCatalogCategoryManager = (options?: UseCatalogCategoryManagerOptions) => {
  const skipListLoad = options?.skipListLoad === true;
  const [categories, setCategories] = useState<CategoryRecord[]>(() => []);
  const [loading, setLoading] = useState(!skipListLoad);
  const [refreshing, setRefreshing] = useState(false);
  const [managerVisible, setManagerVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryRecord | null>(null);
  const [draft, setDraft] = useState<CategoryDraft>(() => emptyCategoryDraft());
  const [saving, setSaving] = useState(false);
  const [togglingCategoryIds, setTogglingCategoryIds] = useState<Set<string>>(() => new Set());
  const toggleInFlightRef = useRef(0);

  const refreshCategories = useCallback(async () => {
    if (skipListLoad) return;
    setRefreshing(true);
    try {
      const rows = await listCategories();
      setCategories(rows);
    } catch (error) {
      console.warn('[Catalog] No se pudieron refrescar categorías', error);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [skipListLoad]);

  useEffect(() => {
    if (skipListLoad) return;
    let mounted = true;

    const run = async () => {
      try {
        const rows = await listCategories();
        if (!mounted) return;
        if (rows.length > 0) {
          setCategories(rows);
        }
      } catch (error) {
        console.warn('[Catalog] No se pudieron cargar categorías', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void run();

    return () => {
      mounted = false;
    };
  }, [skipListLoad]);

  useEffect(() => {
    if (skipListLoad) return;
    const unsubscribeCatalogSynced = subscribeCatalogSynced(() => {
      void listCategories().then((rows) => {
        setCategories(rows);
      });
    });
    return unsubscribeCatalogSynced;
  }, [skipListLoad]);

  const openManager = useCallback(() => {
    setManagerVisible(true);
    void listCategories().then((rows) => {
      if (rows.length > 0) setCategories(rows);
    });
  }, []);

  const closeManager = useCallback(() => {
    setManagerVisible(false);
    setEditingCategory(null);
    setDraft(emptyCategoryDraft());
  }, []);

  const openCreateCategory = useCallback(() => {
    setEditingCategory(null);
    setDraft(emptyCategoryDraft());
    setManagerVisible(true);
  }, []);

  const openEditCategory = useCallback((category: CategoryRecord) => {
    setEditingCategory(category);
    setDraft(toDraft(category));
    setManagerVisible(true);
  }, []);

  const commitSave = useCallback(async () => {
    const name = draft.name.trim();

    if (!name) {
      throw new Error('Ingresá un nombre para la categoría.');
    }

    const parentId = draft.parentId;
    const code = draft.code.trim() || null;
    let defaultMarginPercent: number | null = null;
    if (draft.defaultMarginPercent.trim()) {
      const parsed = parseDecimalInput(draft.defaultMarginPercent.trim(), { min: 0, max: 100 });
      if (parsed === null) {
        throw new Error('Ingresá un margen válido entre 0 y 100 (ej. 25 o 25,5).');
      }
      defaultMarginPercent = parsed;
    }
    const description = draft.description.trim() || null;
    const imageUrl = draft.imageUrl.trim() || null;

    if (editingCategory) {
      await updateCategory(editingCategory.id, {
        name,
        appliesTo: draft.appliesTo,
        parentId,
        code,
        defaultMarginPercent,
        description,
        imageUrl,
        isRestricted: draft.isRestricted,
      });
      return;
    }

    await createCategory({
      name,
      appliesTo: draft.appliesTo,
      parentId,
      code,
      defaultMarginPercent,
      description,
      imageUrl,
      isRestricted: draft.isRestricted,
    });
  }, [draft.name, draft.appliesTo, draft.parentId, draft.code, draft.defaultMarginPercent, draft.description, draft.imageUrl, draft.isRestricted, editingCategory]);

  const saveCategory = useCallback(async (): Promise<boolean> => {
    const wasEditing = Boolean(editingCategory);
    try {
      setSaving(true);
      await commitSave();
      await refreshCategories();
      setDraft(emptyCategoryDraft());
      setEditingCategory(null);
      if (wasEditing) {
        showSuccess('Categoría actualizada', 'Los cambios ya están en la lista.');
      } else {
        showSuccess('Categoría creada', 'Ya podés asignarla a productos.');
      }
      return true;
    } catch (error: any) {
      const message = error?.message ?? 'Ocurrió un error';
      showError('No pudimos guardar', message);
      return false;
    } finally {
      setSaving(false);
    }
  }, [commitSave, editingCategory, refreshCategories]);

  const applyCategoryActive = useCallback(
    async (category: CategoryRecord, nextActive: boolean, toggleOptions?: ToggleCategoryActiveOptions) => {
      const lockIds = new Set([category.id, ...(toggleOptions?.togglingIds ?? [])]);
      setTogglingCategoryIds((prev) => {
        const next = new Set(prev);
        for (const id of lockIds) next.add(id);
        return next;
      });
      toggleInFlightRef.current += 1;
      try {
        await setCategoryActive(category.id, nextActive);

        const isParent = !category.parentId;
        const childCount = toggleOptions?.childCount ?? 0;

        if (nextActive) {
          if (isParent && childCount > 0) {
            showSuccess(
              `${category.name} y ${childCount} subcategoría${childCount === 1 ? '' : 's'} visibles`,
              'Volvieron a aparecer en filtros y selectores.',
            );
          } else if (toggleOptions?.wokeParent) {
            showSuccess(
              'Subcategoría activada',
              'También activamos la categoría.',
            );
          } else {
            showSuccess('Categoría activada', 'Volvió a aparecer en la lista.');
          }
          return;
        }

        if (isParent && childCount > 0) {
          const base = `${category.name} y ${childCount} subcategoría${childCount === 1 ? '' : 's'} ocultas`;
          showSuccess(
            base,
            toggleOptions?.filterState === 'active'
              ? 'Para reactivarlas, andá a la sección «Inactivas».'
              : 'Ya no aparecen en filtros y selectores.',
          );
          return;
        }

        showSuccess('Categoría desactivada', 'Ya no aparecerá en filtros y selectores.');
      } catch (error: any) {
        presentUnexpectedFailure({ module: 'catalog', op: 'no-pudimos-cambiar-el-estado', error: error, title: 'No pudimos cambiar el estado', userMessage: 'Ocurrió un error' });
        throw error;
      } finally {
        setTogglingCategoryIds((prev) => {
          const next = new Set(prev);
          for (const id of lockIds) next.delete(id);
          return next;
        });
        toggleInFlightRef.current = Math.max(0, toggleInFlightRef.current - 1);
        if (toggleInFlightRef.current === 0) {
          if (options?.onCategoriesChanged) {
            options.onCategoriesChanged();
          } else {
            void refreshCategories();
          }
        }
      }
    },
    [options, refreshCategories]
  );

  const toggleCategoryActive = useCallback(
    (category: CategoryRecord, nextActive: boolean, toggleOptions?: ToggleCategoryActiveOptions) =>
      applyCategoryActive(category, nextActive, toggleOptions),
    [applyCategoryActive],
  );

  const [reordering, setReordering] = useState(false);

  /** Persiste el orden propio de la org para un grupo de hermanos (padres o hijas de un padre). */
  const reorderCategories = useCallback(
    async (items: { id: string; sortOrder: number }[]): Promise<boolean> => {
      if (items.length === 0 || reordering) return false;
      setReordering(true);
      try {
        await reorderCategorySortOrders(items);
        showSuccess('Orden actualizado', 'Se ve en tu tienda online y en los filtros del catálogo.');
        options?.onCategoriesChanged?.();
        return true;
      } catch (error: any) {
        presentUnexpectedFailure({
          module: 'catalog',
          op: 'no-pudimos-guardar-el-orden',
          error,
          title: 'No pudimos guardar el orden',
          userMessage: error?.message ?? 'Ocurrió un error',
        });
        return false;
      } finally {
        setReordering(false);
      }
    },
    [options, reordering],
  );

  const [imageUploading, setImageUploading] = useState(false);

  const handlePickCategoryImage = useCallback(async () => {
    try {
      const picked = await pickProductImage();
      if (!picked) return;

      setImageUploading(true);

      let categoryId = editingCategory?.id ?? '';
      if (!categoryId) {
        categoryId = `cat-${crypto.randomUUID()}`;
      }

      const orgId = await requireSessionOrganizationId();
      const optimized = await optimizeProductImage(picked.uri);
      const publicUrl = await uploadCategoryImage({
        orgId,
        categoryId,
        optimized,
      });

      setDraft((prev) => ({ ...prev, imageUrl: publicUrl }));
    } catch (err) {
      if (isProductImageValidationError(err)) {
        showError('Imagen no válida', err.message);
      } else {
        presentUnexpectedFailure({
          module: 'catalog',
          op: 'category.image_upload',
          error: err,
          title: 'No se pudo subir la imagen',
          userMessage: 'No pudimos subir la imagen. Reintentá en un momento.',
        });
      }
    } finally {
      setImageUploading(false);
    }
  }, [editingCategory?.id, setDraft]);

  const handleRemoveCategoryImage = useCallback(async () => {
    const prevUrl = draft.imageUrl;
    setDraft((prev) => ({ ...prev, imageUrl: '' }));

    if (editingCategory?.id && prevUrl) {
      try {
        const orgId = await requireSessionOrganizationId();
        await removeCategoryImage({ orgId, categoryId: editingCategory.id });
      } catch (err) {
        console.warn('[useCatalogCategoryManager] No se pudo borrar la imagen del bucket', err);
      }
    }
  }, [draft.imageUrl, editingCategory?.id, setDraft]);

  const activeCategoryOptions = useMemo(() => buildCategoryOptions(categories), [categories]);

  return {
    categories,
    loading,
    refreshing,
    refreshCategories,
    managerVisible,
    openManager,
    closeManager,
    openCreateCategory,
    openEditCategory,
    editingCategory,
    draft,
    setDraft,
    saving,
    imageUploading,
    handlePickCategoryImage,
    handleRemoveCategoryImage,
    saveCategory,
    toggleCategoryActive,
    togglingCategoryIds,
    reorderCategories,
    reordering,
    activeCategoryOptions,
  };
};
